import { useEffect } from 'react';
import {
  FIXED_ACTIVITY_NAMES,
  isDbProtocolComposition,
  showsEnrollmentLanding,
} from '@hub/common';
import { isEnrollmentLandingStatus } from 'features/participations/logic/participationStatus';
import { useParticipationSchedule } from 'features/participations/schedule/hooks/useParticipationSchedule';
import {
  clearJustCompletedTask,
  isTaskJustCompleted,
} from 'features/participations/schedule/logic/justCompletedTaskGuard';
import {
  isAutoPresentTask,
  isMissedTask,
} from 'features/participations/schedule/logic/taskAvailability';
import { selectParticipationDocData } from 'features/participations/store/participationSelectors';
import { RoutePath } from 'features/routing/constants';
import { useAppSelector } from 'features/store/hooks';
import { assertExhaustiveMatch } from 'shared/utils/typeUtils';
import type {
  EnrollmentPhase,
  EnrollmentShape,
  EnrollmentStep,
  EnrollmentStepStatus,
  OpenEnrollmentPhase,
} from './types';
import type { DbProtocolComposition, FixedActivityName } from '@hub/common';
import type { EnrollmentLandingStatus } from 'features/participations/logic/participationStatus';

type UseEnrollmentParams = { userId: string; participationId: string };

export type UseEnrollmentResult = {
  /** Everything the landing body renders; undefined while the schedule
   *  resolves (outside the complete phase), or when the participation document
   *  is unusable. The landing shows a spinner in place of the body, or the
   *  full-screen loading screen when the protocol hides the landing. */
  body:
    | {
        phase: OpenEnrollmentPhase;
        steps: EnrollmentStep[];
        /** Which enrollment activities the protocol declares. */
        shape: EnrollmentShape;
      }
    | { phase: 'complete'; shape: EnrollmentShape }
    | undefined;
  /** Route of the phase questionnaire; undefined while the schedule resolves,
   *  when no task is presentable, or in the complete phase. */
  ctaRoute: string | undefined;
  /** True while the CTA waits for the task join to reflect a just-completed
   *  task. */
  isCtaResolving: boolean;
  /** True when the CTA resumes a task the participant already started. */
  isCtaResuming: boolean;
  /** True when the participation carries a missing, invalid, or empty
   *  composition. The landing routes to the error page. */
  isError: boolean;
  /** True when the phase's task is projected and missed (unfinished, window
   *  passed). The landing renders the terminal page instead of the body. */
  isWindowClosed: boolean;
  /** True when the protocol hides the landing; the landing forwards to
   *  `ctaRoute` once settled. */
  skipsLanding: boolean;
  /** True outside the complete phase while the landing's data is mid-update:
   *  the schedule loads or recomputes, a just-completed task awaits its join,
   *  or a done prescreener awaits its status update. */
  isSettling: boolean;
};

// The three shapes the landing renders. A composition declaring neither
// activity is undefined — the landing has nothing to enroll into.
const shapeOf = (
  composition: DbProtocolComposition
): EnrollmentShape | undefined => {
  if (composition.prescreener) {
    return composition.icf ? 'both' : 'prescreener-only';
  }
  return composition.icf ? 'icf-only' : undefined;
};

// Which protocol shapes each landing status can carry; the backend writes
// consenting only with both steps, consented only with an ICF, pre-screened
// only for a prescreener-only protocol.
const SHAPES_FOR_STATUS: Record<
  EnrollmentLandingStatus,
  readonly EnrollmentShape[]
> = {
  'pre-screening': ['both', 'prescreener-only', 'icf-only'],
  consenting: ['both'],
  consented: ['both', 'icf-only'],
  'pre-screened': ['prescreener-only'],
};

// The landing renders only for the pre-active statuses (ParticipationShell
// routes every other status elsewhere).
const phaseFor = (
  status: EnrollmentLandingStatus,
  shape: EnrollmentShape,
  prescreenPending: boolean,
  icfDone: boolean
): EnrollmentPhase => {
  if (!SHAPES_FOR_STATUS[status].includes(shape)) {
    throw new Error(
      `[ASSERT FAILED] useEnrollment: status ${status} cannot carry a ${shape} protocol`
    );
  }

  switch (status) {
    case 'pre-screening':
      if (shape === 'icf-only') {
        return icfDone ? 'complete' : 'consent';
      }
      return 'prescreen';
    case 'consenting':
      if (icfDone) return 'complete';
      return prescreenPending ? 'prescreen' : 'consent';
    case 'consented':
    case 'pre-screened':
      return 'complete';
    default:
      return assertExhaustiveMatch(
        '[ASSERT FAILED] useEnrollment: unhandled participation status',
        status
      );
  }
};

const STEP_STATUSES: Record<
  OpenEnrollmentPhase,
  [EnrollmentStepStatus, EnrollmentStepStatus]
> = {
  prescreen: ['current', 'upcoming'],
  consent: ['done', 'current'],
};

// The one task each phase waits on; `complete` waits on none.
const PHASE_TASK: Record<EnrollmentPhase, FixedActivityName | undefined> = {
  prescreen: FIXED_ACTIVITY_NAMES.prescreener,
  consent: FIXED_ACTIVITY_NAMES.icf,
  complete: undefined,
};

const buildSteps = (
  shape: EnrollmentShape,
  phase: OpenEnrollmentPhase
): EnrollmentStep[] => {
  if (shape === 'both') {
    const [prescreening, consent] = STEP_STATUSES[phase];
    return [
      { id: 'prescreening', status: prescreening },
      { id: 'consent', status: consent },
    ];
  }
  const id = shape === 'prescreener-only' ? 'prescreening' : 'consent';
  return [{ id, status: 'current' }];
};

/** Data seam for the enrollment landing: phase/steps from the participation
 *  status (+ ICF progress), CTA from the schedule's auto-present task. */
export const useEnrollment = ({
  userId,
  participationId,
}: UseEnrollmentParams): UseEnrollmentResult => {
  const participation = useAppSelector(selectParticipationDocData);
  const { tasks, isLoading, isRefreshing } = useParticipationSchedule({
    userId,
    participationId,
  });
  const skipsLanding = !showsEnrollmentLanding(participation);

  const shape = isDbProtocolComposition(participation.composition)
    ? shapeOf(participation.composition)
    : undefined;

  const icfDone =
    tasks.some(
      task =>
        task.activityName === FIXED_ACTIVITY_NAMES.icf &&
        task.progress === 'done'
    ) || isTaskJustCompleted(participationId, FIXED_ACTIVITY_NAMES.icf);

  // A prescreener submission writes the participation status (consenting or
  // pre-screened) while the task can still be in its post-submission phase, so
  // the participation status alone does not settle the pre-screening step.
  // Holding on presentability keeps the landing
  // consistent: a prescreener the CTA cannot offer (window closed, not
  // auto-present) never holds the phase.
  const prescreenerTask = tasks.find(
    task => task.activityName === FIXED_ACTIVITY_NAMES.prescreener
  );
  const prescreenPending =
    prescreenerTask !== undefined &&
    isAutoPresentTask(prescreenerTask) &&
    !isTaskJustCompleted(participationId, FIXED_ACTIVITY_NAMES.prescreener);

  // Release the just-completed guard once the join reflects the terminal
  // state; the task is then excluded by isAutoPresentTask on its own.
  useEffect(() => {
    if (
      tasks.some(
        task =>
          task.progress === 'done' &&
          isTaskJustCompleted(participationId, task.activityName)
      )
    ) {
      clearJustCompletedTask();
    }
  }, [tasks, participationId]);

  if (shape === undefined) {
    return {
      body: undefined,
      ctaRoute: undefined,
      isCtaResolving: false,
      isCtaResuming: false,
      isError: true,
      isWindowClosed: false,
      skipsLanding,
      isSettling: false,
    };
  }

  if (!isEnrollmentLandingStatus(participation.status)) {
    throw new Error(
      `[ASSERT FAILED] useEnrollment: unsupported participation status: ${participation.status}`
    );
  }

  const phase = phaseFor(
    participation.status,
    shape,
    prescreenPending,
    icfDone
  );
  const body: UseEnrollmentResult['body'] =
    phase === 'complete'
      ? { phase, shape }
      : isLoading
        ? undefined
        : { phase, steps: buildSteps(shape, phase), shape };

  const phaseTask = tasks.find(task => task.activityName === PHASE_TASK[phase]);
  const isWindowClosed =
    !isLoading && phaseTask !== undefined && isMissedTask(phaseTask);

  // A just-completed task can still read as presentable while the
  // tasks-collection listener lags the completion write — the CTA must never
  // target it. With no other candidate the CTA stays in its resolving state
  // until the join catches up.
  const presentableTasks =
    isLoading || phase === 'complete' || isWindowClosed
      ? []
      : tasks.filter(isAutoPresentTask);
  const candidates = presentableTasks.filter(
    task => !isTaskJustCompleted(participationId, task.activityName)
  );
  // In the prescreen phase the CTA prefers the prescreener so its label and
  // destination stay aligned when it's available; every other phase takes the
  // first match.
  const target =
    (phase === 'prescreen'
      ? candidates.find(
          task => task.activityName === FIXED_ACTIVITY_NAMES.prescreener
        )
      : undefined) ?? candidates.at(0);
  const isCtaResolving = target === undefined && presentableTasks.length > 0;
  const awaitsStatusUpdate =
    phase === 'prescreen' && prescreenerTask?.progress === 'done';
  const isSettling =
    phase !== 'complete' &&
    (body === undefined ||
      isCtaResolving ||
      isRefreshing ||
      awaitsStatusUpdate);

  return {
    body,
    ctaRoute: target
      ? RoutePath.ParticipationTask(participationId, target.activityName)
      : undefined,
    isCtaResolving,
    isCtaResuming: target?.progress === 'in-progress',
    isError: false,
    isWindowClosed,
    skipsLanding,
    isSettling,
  };
};
